import { apiClient } from '../services/api/apiClient';

type SpeechPonyfillFactory = (options: unknown) => unknown;

/**
 * Fetches an Azure Speech authorization token from the backend and wraps it
 * into WebChat's Cognitive Services ponyfill so a microphone button appears
 * in WebChat's input and the bot's replies can be read aloud.
 *
 * The Azure subscription key stays server-side — the browser only sees a
 * short-lived (~10 min) authorization token. `authorizationToken` is
 * passed as an async fetcher rather than a bare string, which is what the
 * current CDN build of WebChat expects; passing a plain string used to
 * work but now surfaces "web-speech-cognitive-services: Credentials must
 * be specified" from inside WebChat's useMemo hook, which silently blanks
 * the whole chat container.
 *
 * Note on silence timeout: the CDN `webchat-es5.js` build of BotFramework
 * hides the Azure Speech SDK's SpeechConfig, so `Speech_SegmentationSilenceTimeoutMs`
 * cannot be tuned from here. WebChat submits as soon as the recognizer
 * emits `isFinal=true` — attempts to force `continuous = true` on the
 * SpeechRecognition subclass break WebChat's dictate state machine after
 * a couple of activations (recognizer stays "running", mic refuses to
 * re-arm, ScriptProcessorNode instances leak). We accept the default ~1s
 * silence window here and expose the mic as a hold/click UX in the caller.
 *
 * Returns `null` when the CDN build doesn't expose the factory, the
 * backend has no Speech credentials configured, the returned token/region
 * are empty, or building the factory throws — so the caller can still
 * render a text-only WebChat instead of failing.
 */
export async function createSpeechPonyfillFactory(): Promise<SpeechPonyfillFactory | null> {
  const factoryBuilder = window.WebChat?.createCognitiveServicesSpeechServicesPonyfillFactory;
  if (typeof factoryBuilder !== 'function') return null;

  let token = '';
  let region = '';
  try {
    const response = await apiClient.post<{ token?: string; region?: string }>(
      '/api/assistant/speech-token',
      {}
    );
    token = typeof response?.token === 'string' ? response.token.trim() : '';
    region = typeof response?.region === 'string' ? response.region.trim() : '';
  } catch {
    return null;
  }
  if (!token || !region) return null;

  try {
    return factoryBuilder({
      credentials: () => Promise.resolve({ authorizationToken: token, region }),
    }) as SpeechPonyfillFactory;
  } catch {
    return null;
  }
}
